import React, { useState } from 'react';
import { supabase } from '../../../services/supabaseClient';

const Section = ({ title, items, renderItem, emptyLabel }) => (
    <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
        <div className="bg-gray-900/50 p-4 border-b border-gray-700">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">{title}</h3>
        </div>
        <div className="p-4">
            {items && items.length > 0 ? (
                <ul className="space-y-3">
                    {items.map(renderItem)}
                </ul>
            ) : (
                <div className="text-sm text-gray-400 text-center py-6">{emptyLabel}</div>
            )}
        </div>
    </div>
);

const ClientFamily = ({ clientId, dependents = [], guardians = [], siblings = [], visaRefusals = [], onRefresh }) => {
    const [saving, setSaving] = useState(false);

    const [newDependent, setNewDependent] = useState({
        full_name: '',
        dob: '',
        passport_no: '',
        passport_issue_date: '',
        passport_expiry_date: '',
        has_previous_rejections: false
    });
    const [editingDependentId, setEditingDependentId] = useState(null);
    const [dependentDraft, setDependentDraft] = useState(null);

    const [newGuardian, setNewGuardian] = useState({ name: '', relation: '' });
    const [editingGuardianId, setEditingGuardianId] = useState(null);
    const [guardianDraft, setGuardianDraft] = useState(null);

    const [newSibling, setNewSibling] = useState({
        full_name: '',
        dob: '',
        institution: '',
        program: '',
        student_id: '',
        studying_same_institute: false
    });
    const [editingSiblingId, setEditingSiblingId] = useState(null);
    const [siblingDraft, setSiblingDraft] = useState(null);

    const [newRefusal, setNewRefusal] = useState({
        country: '',
        visa_type: '',
        rejection_date: '',
        rejection_reason: '',
        has_sponsor: false
    });
    const [editingRefusalId, setEditingRefusalId] = useState(null);
    const [refusalDraft, setRefusalDraft] = useState(null);

    const wrap = async (fn) => {
        if (!clientId) return;
        setSaving(true);
        try {
            await fn();
            if (onRefresh) await onRefresh();
        } finally {
            setSaving(false);
        }
    };

    const parseBool = (val) => !!val;

    return (
        <div className="space-y-6">
            {/* Dependents */}
            <Section
                title="Dependents"
                items={dependents}
                emptyLabel="No dependents recorded"
                renderItem={(d) => {
                    const isEditing = editingDependentId === d.id;
                    const draft = isEditing ? (dependentDraft || {}) : d;
                    return (
                        <li key={d.id} className="p-3 bg-gray-700/50 rounded border border-gray-600">
                            <div className="flex items-center justify-between mb-2">
                                {isEditing ? (
                                    <input
                                        className="text-sm text-white font-mono bg-gray-800 border border-gray-600 rounded px-2 py-1 w-full mr-2"
                                        value={draft.full_name || ''}
                                        onChange={(e) => setDependentDraft({ ...draft, full_name: e.target.value })}
                                        placeholder="Full name"
                                    />
                                ) : (
                                    <span className="text-sm text-white font-mono">{d.full_name || 'Unnamed dependent'}</span>
                                )}
                                <div className="flex items-center gap-2">
                                    {d.has_previous_rejections && !isEditing && (
                                        <span className="px-2 py-0.5 rounded-full bg-red-900/30 text-red-300 border border-red-800 text-xs">
                                            Previous visa rejection
                                        </span>
                                    )}
                                    {isEditing ? (
                                        <>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() =>
                                                    wrap(async () => {
                                                        await supabase
                                                            .from('client_dependents')
                                                            .update({
                                                                full_name: draft.full_name || null,
                                                                dob: draft.dob || null,
                                                                passport_no: draft.passport_no || null,
                                                                passport_issue_date: draft.passport_issue_date || null,
                                                                passport_expiry_date: draft.passport_expiry_date || null,
                                                                has_previous_rejections: parseBool(draft.has_previous_rejections)
                                                            })
                                                            .eq('id', d.id);
                                                        setEditingDependentId(null);
                                                        setDependentDraft(null);
                                                    })
                                                }
                                                className="text-xs px-2 py-1 rounded bg-green-600 text-white border border-green-500"
                                            >
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    setEditingDependentId(null);
                                                    setDependentDraft(null);
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200 border border-gray-500"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    setEditingDependentId(d.id);
                                                    setDependentDraft({
                                                        full_name: d.full_name || '',
                                                        dob: d.dob || '',
                                                        passport_no: d.passport_no || '',
                                                        passport_issue_date: d.passport_issue_date || '',
                                                        passport_expiry_date: d.passport_expiry_date || '',
                                                        has_previous_rejections: !!d.has_previous_rejections
                                                    });
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-blue-700 text-white border border-blue-500"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    if (!window.confirm('Remove this dependent?')) return;
                                                    wrap(async () => {
                                                        await supabase.from('client_dependents').delete().eq('id', d.id);
                                                    });
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-red-700 text-white border border-red-500"
                                            >
                                                Remove
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs text-gray-400">
                                <div>
                                    <div className="uppercase text-[10px] text-gray-500">DOB</div>
                                    {isEditing ? (
                                        <input
                                            type="date"
                                            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                            value={draft.dob || ''}
                                            onChange={(e) => setDependentDraft({ ...draft, dob: e.target.value })}
                                        />
                                    ) : (
                                        <div>{d.dob || '-'}</div>
                                    )}
                                </div>
                                <div>
                                    <div className="uppercase text-[10px] text-gray-500">Passport</div>
                                    {isEditing ? (
                                        <input
                                            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                            value={draft.passport_no || ''}
                                            onChange={(e) => setDependentDraft({ ...draft, passport_no: e.target.value })}
                                            placeholder="Passport number"
                                        />
                                    ) : (
                                        <div>{d.passport_no || '-'}</div>
                                    )}
                                </div>
                                <div>
                                    <div className="uppercase text-[10px] text-gray-500">Issue Date</div>
                                    {isEditing ? (
                                        <input
                                            type="date"
                                            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                            value={draft.passport_issue_date || ''}
                                            onChange={(e) => setDependentDraft({ ...draft, passport_issue_date: e.target.value })}
                                        />
                                    ) : (
                                        <div>{d.passport_issue_date || '-'}</div>
                                    )}
                                </div>
                                <div>
                                    <div className="uppercase text-[10px] text-gray-500">Expiry Date</div>
                                    {isEditing ? (
                                        <input
                                            type="date"
                                            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                            value={draft.passport_expiry_date || ''}
                                            onChange={(e) => setDependentDraft({ ...draft, passport_expiry_date: e.target.value })}
                                        />
                                    ) : (
                                        <div>{d.passport_expiry_date || '-'}</div>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                }}
            />
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Add Dependent</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        placeholder="Full name"
                        value={newDependent.full_name}
                        onChange={(e) => setNewDependent({ ...newDependent, full_name: e.target.value })}
                    />
                    <input
                        type="date"
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        value={newDependent.dob}
                        onChange={(e) => setNewDependent({ ...newDependent, dob: e.target.value })}
                    />
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        placeholder="Passport no."
                        value={newDependent.passport_no}
                        onChange={(e) => setNewDependent({ ...newDependent, passport_no: e.target.value })}
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-300">
                        <input
                            type="checkbox"
                            checked={newDependent.has_previous_rejections}
                            onChange={(e) => setNewDependent({ ...newDependent, has_previous_rejections: e.target.checked })}
                            className="rounded border-gray-600 bg-gray-900"
                        />
                        Previous visa rejection
                    </label>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
                    <div>
                        <div className="text-[10px] uppercase text-gray-500 mb-1">Issue Date</div>
                        <input
                            type="date"
                            className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white w-full"
                            value={newDependent.passport_issue_date}
                            onChange={(e) => setNewDependent({ ...newDependent, passport_issue_date: e.target.value })}
                        />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase text-gray-500 mb-1">Expiry Date</div>
                        <input
                            type="date"
                            className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white w-full"
                            value={newDependent.passport_expiry_date}
                            onChange={(e) => setNewDependent({ ...newDependent, passport_expiry_date: e.target.value })}
                        />
                    </div>
                </div>
                <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                        if (!newDependent.full_name.trim()) return;
                        wrap(async () => {
                            await supabase.from('client_dependents').insert([{
                                client_id: clientId,
                                full_name: newDependent.full_name || null,
                                dob: newDependent.dob || null,
                                passport_no: newDependent.passport_no || null,
                                passport_issue_date: newDependent.passport_issue_date || null,
                                passport_expiry_date: newDependent.passport_expiry_date || null,
                                has_previous_rejections: !!newDependent.has_previous_rejections
                            }]);
                            setNewDependent({
                                full_name: '',
                                dob: '',
                                passport_no: '',
                                passport_issue_date: '',
                                passport_expiry_date: '',
                                has_previous_rejections: false
                            });
                        });
                    }}
                    className="mt-2 px-3 py-1.5 rounded text-xs font-bold bg-blue-600 text-white border border-blue-500"
                >
                    + Add Dependent
                </button>
            </div>

            {/* Guardians */}
            <Section
                title="Guardians / Sponsors"
                items={guardians}
                emptyLabel="No guardians or sponsors recorded"
                renderItem={(g) => {
                    const isEditing = editingGuardianId === g.id;
                    const draft = isEditing ? (guardianDraft || {}) : g;
                    return (
                        <li key={g.id} className="p-3 bg-gray-700/50 rounded border border-gray-600">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex-1 mr-2">
                                    {isEditing ? (
                                        <>
                                            <input
                                                className="w-full mb-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                value={draft.name || ''}
                                                onChange={(e) => setGuardianDraft({ ...draft, name: e.target.value })}
                                                placeholder="Name"
                                            />
                                            <input
                                                className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                value={draft.relation || ''}
                                                onChange={(e) => setGuardianDraft({ ...draft, relation: e.target.value })}
                                                placeholder="Relation"
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-sm text-white font-mono">{g.name || 'Unnamed guardian'}</div>
                                            <div className="text-xs text-gray-400">{g.relation || 'Relation not specified'}</div>
                                        </>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {isEditing ? (
                                        <>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() =>
                                                    wrap(async () => {
                                                        await supabase.from('client_guardians').update({
                                                            name: draft.name || null,
                                                            relation: draft.relation || null
                                                        }).eq('id', g.id);
                                                        setEditingGuardianId(null);
                                                        setGuardianDraft(null);
                                                    })
                                                }
                                                className="text-xs px-2 py-1 rounded bg-green-600 text-white border border-green-500"
                                            >
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    setEditingGuardianId(null);
                                                    setGuardianDraft(null);
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200 border border-gray-500"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    setEditingGuardianId(g.id);
                                                    setGuardianDraft({
                                                        name: g.name || '',
                                                        relation: g.relation || ''
                                                    });
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-blue-700 text-white border border-blue-500"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    if (!window.confirm('Remove this guardian/sponsor?')) return;
                                                    wrap(async () => {
                                                        await supabase.from('client_guardians').delete().eq('id', g.id);
                                                    });
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-red-700 text-white border border-red-500"
                                            >
                                                Remove
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                }}
            />
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Add Guardian / Sponsor</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        placeholder="Name"
                        value={newGuardian.name}
                        onChange={(e) => setNewGuardian({ ...newGuardian, name: e.target.value })}
                    />
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        placeholder="Relation"
                        value={newGuardian.relation}
                        onChange={(e) => setNewGuardian({ ...newGuardian, relation: e.target.value })}
                    />
                </div>
                <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                        if (!newGuardian.name.trim() && !newGuardian.relation.trim()) return;
                        wrap(async () => {
                            await supabase.from('client_guardians').insert([{
                                client_id: clientId,
                                name: newGuardian.name || null,
                                relation: newGuardian.relation || null
                            }]);
                            setNewGuardian({ name: '', relation: '' });
                        });
                    }}
                    className="mt-2 px-3 py-1.5 rounded text-xs font-bold bg-blue-600 text-white border border-blue-500"
                >
                    + Add Guardian
                </button>
            </div>

            {/* Siblings */}
            <Section
                title="Siblings"
                items={siblings}
                emptyLabel="No siblings recorded"
                renderItem={(s) => {
                    const isEditing = editingSiblingId === s.id;
                    const draft = isEditing ? (siblingDraft || {}) : s;
                    return (
                        <li key={s.id} className="p-3 bg-gray-700/50 rounded border border-gray-600">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex-1 mr-2">
                                    {isEditing ? (
                                        <>
                                            <input
                                                className="w-full mb-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                value={draft.full_name || ''}
                                                onChange={(e) => setSiblingDraft({ ...draft, full_name: e.target.value })}
                                                placeholder="Full name"
                                            />
                                            <input
                                                className="w-full mb-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                value={draft.institution || ''}
                                                onChange={(e) => setSiblingDraft({ ...draft, institution: e.target.value })}
                                                placeholder="Institution"
                                            />
                                            <input
                                                className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                value={draft.program || ''}
                                                onChange={(e) => setSiblingDraft({ ...draft, program: e.target.value })}
                                                placeholder="Program"
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm text-white font-mono">{s.full_name || 'Unnamed sibling'}</span>
                                                {s.studying_same_institute && (
                                                    <span className="px-2 py-0.5 rounded-full bg-blue-900/30 text-blue-300 border border-blue-800 text-xs">
                                                        Same institute
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-400 space-y-0.5">
                                                {s.dob && <div>DOB: {s.dob}</div>}
                                                {s.institution && <div>Institution: {s.institution}</div>}
                                                {s.program && <div>Program: {s.program}</div>}
                                            </div>
                                        </>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {isEditing ? (
                                        <>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() =>
                                                    wrap(async () => {
                                                        await supabase.from('client_siblings').update({
                                                            full_name: draft.full_name || null,
                                                            dob: draft.dob || null,
                                                            institution: draft.institution || null,
                                                            program: draft.program || null,
                                                            student_id: draft.student_id || null,
                                                            studying_same_institute: parseBool(draft.studying_same_institute)
                                                        }).eq('id', s.id);
                                                        setEditingSiblingId(null);
                                                        setSiblingDraft(null);
                                                    })
                                                }
                                                className="text-xs px-2 py-1 rounded bg-green-600 text-white border border-green-500"
                                            >
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    setEditingSiblingId(null);
                                                    setSiblingDraft(null);
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200 border border-gray-500"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    setEditingSiblingId(s.id);
                                                    setSiblingDraft({
                                                        full_name: s.full_name || '',
                                                        dob: s.dob || '',
                                                        institution: s.institution || '',
                                                        program: s.program || '',
                                                        student_id: s.student_id || '',
                                                        studying_same_institute: !!s.studying_same_institute
                                                    });
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-blue-700 text-white border border-blue-500"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    if (!window.confirm('Remove this sibling?')) return;
                                                    wrap(async () => {
                                                        await supabase.from('client_siblings').delete().eq('id', s.id);
                                                    });
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-red-700 text-white border border-red-500"
                                            >
                                                Remove
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-400">
                                <div>
                                    <div className="uppercase text-[10px] text-gray-500">DOB</div>
                                    {isEditing ? (
                                        <input
                                            type="date"
                                            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                            value={draft.dob || ''}
                                            onChange={(e) => setSiblingDraft({ ...draft, dob: e.target.value })}
                                        />
                                    ) : (
                                        <div>{s.dob || '-'}</div>
                                    )}
                                </div>
                                <div>
                                    <div className="uppercase text-[10px] text-gray-500">Student ID</div>
                                    {isEditing ? (
                                        <input
                                            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                            value={draft.student_id || ''}
                                            onChange={(e) => setSiblingDraft({ ...draft, student_id: e.target.value })}
                                        />
                                    ) : (
                                        <div>{s.student_id || '-'}</div>
                                    )}
                                </div>
                                <div className="flex items-center">
                                    {isEditing ? (
                                        <label className="flex items-center gap-2 text-xs text-gray-300">
                                            <input
                                                type="checkbox"
                                                checked={!!draft.studying_same_institute}
                                                onChange={(e) => setSiblingDraft({ ...draft, studying_same_institute: e.target.checked })}
                                                className="rounded border-gray-600 bg-gray-900"
                                            />
                                            Same institute
                                        </label>
                                    ) : null}
                                </div>
                            </div>
                        </li>
                    );
                }}
            />
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Add Sibling</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        placeholder="Full name"
                        value={newSibling.full_name}
                        onChange={(e) => setNewSibling({ ...newSibling, full_name: e.target.value })}
                    />
                    <input
                        type="date"
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        value={newSibling.dob}
                        onChange={(e) => setNewSibling({ ...newSibling, dob: e.target.value })}
                    />
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        placeholder="Institution"
                        value={newSibling.institution}
                        onChange={(e) => setNewSibling({ ...newSibling, institution: e.target.value })}
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        placeholder="Program"
                        value={newSibling.program}
                        onChange={(e) => setNewSibling({ ...newSibling, program: e.target.value })}
                    />
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        placeholder="Student ID"
                        value={newSibling.student_id}
                        onChange={(e) => setNewSibling({ ...newSibling, student_id: e.target.value })}
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-300">
                        <input
                            type="checkbox"
                            checked={newSibling.studying_same_institute}
                            onChange={(e) => setNewSibling({ ...newSibling, studying_same_institute: e.target.checked })}
                            className="rounded border-gray-600 bg-gray-900"
                        />
                        Same institute
                    </label>
                </div>
                <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                        if (!newSibling.full_name.trim()) return;
                        wrap(async () => {
                            await supabase.from('client_siblings').insert([{
                                client_id: clientId,
                                full_name: newSibling.full_name || null,
                                dob: newSibling.dob || null,
                                institution: newSibling.institution || null,
                                program: newSibling.program || null,
                                student_id: newSibling.student_id || null,
                                studying_same_institute: !!newSibling.studying_same_institute
                            }]);
                            setNewSibling({
                                full_name: '',
                                dob: '',
                                institution: '',
                                program: '',
                                student_id: '',
                                studying_same_institute: false
                            });
                        });
                    }}
                    className="mt-2 px-3 py-1.5 rounded text-xs font-bold bg-blue-600 text-white border border-blue-500"
                >
                    + Add Sibling
                </button>
            </div>

            {/* Previous Visa Rejections */}
            <Section
                title="Previous Visa Rejections"
                items={visaRefusals}
                emptyLabel="No previous visa refusals recorded"
                renderItem={(r) => {
                    const isEditing = editingRefusalId === r.id;
                    const draft = isEditing ? (refusalDraft || {}) : r;
                    return (
                        <li key={r.id} className="p-3 bg-gray-700/50 rounded border border-gray-600">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex-1 mr-2">
                                    {isEditing ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            <input
                                                className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                placeholder="Country"
                                                value={draft.country || ''}
                                                onChange={(e) => setRefusalDraft({ ...draft, country: e.target.value })}
                                            />
                                            <input
                                                className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                                placeholder="Visa type"
                                                value={draft.visa_type || ''}
                                                onChange={(e) => setRefusalDraft({ ...draft, visa_type: e.target.value })}
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm text-white font-mono">
                                                {r.country || 'Country'} • {r.visa_type || 'Visa type'}
                                            </span>
                                            {r.has_sponsor && (
                                                <span className="px-2 py-0.5 rounded-full bg-amber-900/30 text-amber-300 border border-amber-800 text-xs">
                                                    Had sponsor
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {isEditing ? (
                                        <>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() =>
                                                    wrap(async () => {
                                                        await supabase.from('client_visa_refusals').update({
                                                            country: draft.country || null,
                                                            visa_type: draft.visa_type || null,
                                                            rejection_date: draft.rejection_date || null,
                                                            rejection_reason: draft.rejection_reason || null,
                                                            has_sponsor: parseBool(draft.has_sponsor)
                                                        }).eq('id', r.id);
                                                        setEditingRefusalId(null);
                                                        setRefusalDraft(null);
                                                    })
                                                }
                                                className="text-xs px-2 py-1 rounded bg-green-600 text-white border border-green-500"
                                            >
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    setEditingRefusalId(null);
                                                    setRefusalDraft(null);
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-200 border border-gray-500"
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    setEditingRefusalId(r.id);
                                                    setRefusalDraft({
                                                        country: r.country || '',
                                                        visa_type: r.visa_type || '',
                                                        rejection_date: r.rejection_date || '',
                                                        rejection_reason: r.rejection_reason || '',
                                                        has_sponsor: !!r.has_sponsor
                                                    });
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-blue-700 text-white border border-blue-500"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                disabled={saving}
                                                onClick={() => {
                                                    if (!window.confirm('Remove this visa refusal record?')) return;
                                                    wrap(async () => {
                                                        await supabase.from('client_visa_refusals').delete().eq('id', r.id);
                                                    });
                                                }}
                                                className="text-xs px-2 py-1 rounded bg-red-700 text-white border border-red-500"
                                            >
                                                Remove
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-400">
                                <div>
                                    <div className="uppercase text-[10px] text-gray-500">Date</div>
                                    {isEditing ? (
                                        <input
                                            type="date"
                                            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                            value={draft.rejection_date || ''}
                                            onChange={(e) => setRefusalDraft({ ...draft, rejection_date: e.target.value })}
                                        />
                                    ) : (
                                        <div>{r.rejection_date || '-'}</div>
                                    )}
                                </div>
                                <div className="md:col-span-2">
                                    <div className="uppercase text-[10px] text-gray-500">Reason</div>
                                    {isEditing ? (
                                        <input
                                            className="w-full bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                                            value={draft.rejection_reason || ''}
                                            onChange={(e) => setRefusalDraft({ ...draft, rejection_reason: e.target.value })}
                                            placeholder="Reason"
                                        />
                                    ) : (
                                        <div>{r.rejection_reason || '-'}</div>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                }}
            />
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                <div className="text-xs text-gray-400 mb-2 uppercase tracking-wider">Add Previous Visa Rejection</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        placeholder="Country"
                        value={newRefusal.country}
                        onChange={(e) => setNewRefusal({ ...newRefusal, country: e.target.value })}
                    />
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        placeholder="Visa type"
                        value={newRefusal.visa_type}
                        onChange={(e) => setNewRefusal({ ...newRefusal, visa_type: e.target.value })}
                    />
                    <input
                        type="date"
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white"
                        value={newRefusal.rejection_date}
                        onChange={(e) => setNewRefusal({ ...newRefusal, rejection_date: e.target.value })}
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                    <input
                        className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-xs text-white md:col-span-2"
                        placeholder="Reason"
                        value={newRefusal.rejection_reason}
                        onChange={(e) => setNewRefusal({ ...newRefusal, rejection_reason: e.target.value })}
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-300">
                        <input
                            type="checkbox"
                            checked={newRefusal.has_sponsor}
                            onChange={(e) => setNewRefusal({ ...newRefusal, has_sponsor: e.target.checked })}
                            className="rounded border-gray-600 bg-gray-900"
                        />
                        Had sponsor
                    </label>
                </div>
                <button
                    type="button"
                    disabled={saving}
                    onClick={() => {
                        if (!newRefusal.country.trim() && !newRefusal.visa_type.trim() && !newRefusal.rejection_reason.trim()) return;
                        wrap(async () => {
                            await supabase.from('client_visa_refusals').insert([{
                                client_id: clientId,
                                country: newRefusal.country || null,
                                visa_type: newRefusal.visa_type || null,
                                rejection_date: newRefusal.rejection_date || null,
                                rejection_reason: newRefusal.rejection_reason || null,
                                has_sponsor: !!newRefusal.has_sponsor
                            }]);
                            setNewRefusal({
                                country: '',
                                visa_type: '',
                                rejection_date: '',
                                rejection_reason: '',
                                has_sponsor: false
                            });
                        });
                    }}
                    className="mt-2 px-3 py-1.5 rounded text-xs font-bold bg-blue-600 text-white border border-blue-500"
                >
                    + Add Rejection
                </button>
            </div>
        </div>
    );
};

export default ClientFamily;
